import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db } from '../lib/firebase';
import { collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, query } from 'firebase/firestore';
import { useUser } from '../context/UserProvider';
import { useTheme } from '../context/ThemeContext';
import { ScreenGuard } from '../components/ScreenGuard';
import ListSelector from '../components/ListSelector';
import ManageListsModal from '../components/ManageListsModal';
import ShareListModal from '../components/ShareListModal';
import { applyLocalDiscount, formatPrice } from '../utils/priceHelpers';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import IDX from '../utils/indices';
import { algoliasearch } from 'algoliasearch';
import DetailsSheetLite from '../components/DetailsSheetLite';
import { Share2, Trash2, FileText, ShieldAlert } from 'lucide-react';
import { generateBuyingListPDF } from '../utils/pdfGenerator';
import LanguagePickerModal from '../components/LanguagePickerModal';
import ConfirmModal from '../components/ConfirmModal';
import {
  checkNeedsPriceSync,
  syncBuyListPrices,
  updateListLastUpdated,
  loadPriceData,
} from '../services/buyListPriceSync';

// Pagination: 10 items per page
const ITEMS_PER_PAGE = 10;

// Compute display price in the user's selected currency using stored prices map
function computeItemPriceLocal(item, currency, pricing) {
  const cur = String(currency || 'USD').toUpperCase();
  const key = `price${cur}`;
  let localBase =
    typeof item?.prices?.[key] === 'number'
      ? item.prices[key]
      : (typeof item?.[key] === 'number' ? item[key] : null);
  let usdBase =
    typeof item?.prices?.priceUSD === 'number'
      ? item.prices.priceUSD
      : (typeof item?.priceUSD === 'number' ? item.priceUSD : null);
  if (typeof localBase !== 'number' || typeof usdBase !== 'number') return null;
  // Apply item-level discount (stored on the buy list document)
  const itemDiscount = Number(item?.discount) || 0;
  if (itemDiscount > 0 && itemDiscount <= 100) {
    const ratio = 1 - itemDiscount / 100;
    localBase = localBase * ratio;
    usdBase = usdBase * ratio;
  }
  return applyLocalDiscount(localBase, usdBase, pricing);
}

// Qty shim for legacy + CALCULATOR (app treats missing as 1)
function getQty(item) {
  const n = Number(item?.total);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

export default function BuyingLists() {
  const { ready, uid, settings, pricing, basePrivRole } = useUser();
  const { theme: th, isForest } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const effectiveRole = String(basePrivRole || (settings?.discount || 'free')).toLowerCase();
  const currency = String(settings?.currency || 'USD').toUpperCase();

  // Toggle state for Boards/Converters
  const [showBoards, setShowBoards] = React.useState(false);
  const [active, setActive] = React.useState("converters");
  const [clearConfirmOpen, setClearConfirmOpen] = React.useState(false);

  // Sync active state with showBoards toggle
  React.useEffect(() => {
    setActive(showBoards ? "boards" : "converters");
  }, [showBoards]);

  // Sync toggle state with Layout header button
  React.useEffect(() => {
    const handleHeaderToggle = (event) => {
      setShowBoards(event.detail.showBoards);
    };
    window.addEventListener('headerToggleBuyingLists', handleHeaderToggle);
    return () => window.removeEventListener('headerToggleBuyingLists', handleHeaderToggle);
  }, []);

  React.useEffect(() => {
    // Notify Layout of state changes
    window.dispatchEvent(new CustomEvent('buyingListsToggleChange', { detail: { showBoards } }));
  }, [showBoards]);

  // Ensure boards lists schema exists when switching to Boards tab
  React.useEffect(() => {
    if (!uid || active !== 'boards') return;
    (async () => {
      try {
        const userRef = doc(db, 'users', uid);
        const snap = await getDoc(userRef);
        const d = snap.exists() ? (snap.data() || {}) : {};
        let listsB = d.listsBoards || {};
        let selB = d.selectedListBoard || 'default';
        let changed = false;
        if (!listsB || Object.keys(listsB).length === 0) {
          listsB = { default: { listName: 'default', lastUpdated: Date.now() } };
          selB = 'default';
          changed = true;
        }
        if (!d.selectedListBoard) { selB = 'default'; changed = true; }
        if (changed) {
          await setDoc(userRef, { selectedListBoard: selB, listsBoards: listsB }, { merge: true });
        }
      } catch (e) {
        console.warn('[BuyingLists] ensure boards lists failed', e);
      }
    })();
  }, [uid, active]);

  const [, setLists] = React.useState({});
  const [selected, setSelected] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [manageOpen, setManageOpen] = React.useState(false);
  const [shareOpen, setShareOpen] = React.useState(false);
  const [pdfPickerOpen, setPdfPickerOpen] = React.useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(0);
  const listContainerRef = React.useRef(null);

  // Floating quantity expanded state (for admin to see PT/PD/RH)
  const [qtyExpanded, setQtyExpanded] = React.useState(false);

  // Discount (percent) - can go negative per app requirements
  const [discountPct, setDiscountPct] = React.useState(Number(settings?.buyListDiscount) || 0);
  React.useEffect(() => {
    setDiscountPct(Number(settings?.buyListDiscount) || 0);
  }, [settings?.buyListDiscount]);

  // Discount color: green if positive, red if negative/zero
  const discountColor = discountPct > 0 ? '#4caf50' : discountPct < 0 ? '#ef4444' : (isForest ? '#a5d6a7' : '#6b7280');

  // Metals metrics (PT/PD/RH for converters, AU/AG/PD/CU for boards)
  const [metals, setMetals] = React.useState({ pt: 0, pd: 0, rh: 0 });
  const [boardsMetals, setBoardsMetals] = React.useState({ au: 0, ag: 0, pd: 0, cu: 0 });
  const [metalsLoading, setMetalsLoading] = React.useState(false);
  const metalsFetchIdRef = React.useRef(0);

  // DailyPrices (for PGM per-ounce + FX)
  const [dailyPrices, setDailyPrices] = React.useState(null);
  React.useEffect(() => {
    (async () => {
      try {
        const dSnap = await getDoc(doc(db, 'prices', 'DailyPrices'));
        setDailyPrices(dSnap.exists() ? dSnap.data() : null);
      } catch {
        setDailyPrices(null);
      }
    })();
  }, []);

  // Keep page in sync with UserProvider changes
  React.useEffect(() => {
    if (active === 'boards') {
      if (settings?.listsBoards) setLists(settings.listsBoards);
    } else {
      if (settings?.lists) setLists(settings.lists);
    }
  }, [settings?.lists, settings?.listsBoards, active]);

  React.useEffect(() => {
    if (active === 'boards') {
      if (settings?.selectedListBoard && settings.selectedListBoard !== selected) {
        setSelected(settings.selectedListBoard);
      }
    } else {
      if (settings?.selectedList && settings.selectedList !== selected) {
        setSelected(settings.selectedList);
      }
    }
  }, [settings?.selectedList, settings?.selectedListBoard, selected, active]);

  // Initial load and ensure default
  React.useEffect(() => {
    if (!ready || !uid) return;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const userRef = doc(db, 'users', uid);
        const snap = await getDoc(userRef);
        let data = snap.exists() ? snap.data() : {};

        if (active === 'boards') {
          let userLists = data?.listsBoards || {};
          let selectedList = data?.selectedListBoard || 'default';
          if (!userLists || Object.keys(userLists).length === 0) {
            userLists = { default: { listName: 'default', lastUpdated: Date.now() } };
            await setDoc(userRef, { listsBoards: userLists, selectedListBoard: 'default' }, { merge: true });
            selectedList = 'default';
          }
          setLists(userLists);
          setSelected(selectedList);
        } else {
          let userLists = data?.lists || {};
          let selectedList = data?.selectedList || 'default';
          if (!userLists || Object.keys(userLists).length === 0) {
            userLists = { default: { listName: 'default', lastUpdated: Date.now() } };
            await setDoc(userRef, { lists: userLists, selectedList: 'default' }, { merge: true });
            selectedList = 'default';
          }
          setLists(userLists);
          setSelected(selectedList);
        }
      } catch (e) {
        console.error(e);
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [ready, uid, active]);

  // Load items of selected list
  React.useEffect(() => {
    if (!uid || !selected) { setItems([]); return; }
    (async () => {
      setLoading(true);
      setError('');
      try {
        const collectionPath = active === 'boards' ? 'buyListsBoards' : 'buyLists';
        const col = collection(db, collectionPath, uid, selected);
        const q = query(col);
        const snap = await getDocs(q);
        const out = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setItems(out);
        setCurrentPage(0);
      } catch (e) {
        console.error(e);
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [uid, selected, active]);

  // Price sync: update buy list item prices when market prices have changed
  const [priceSyncing, setPriceSyncing] = React.useState(false);

  React.useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    if (!uid || !selected || items.length === 0 || loading) return;
    // Wait for algolia client to be ready
    if (!algoliaClientRef.current) return;

    let cancelled = false; // Flag to cancel ongoing sync

    (async () => {
      try {
        const { dailyPrices: dp, charges, lastUpdated } = await loadPriceData();
        if (cancelled) return; // Check if cancelled after async operation

        const listsField = active === 'boards' ? 'listsBoards' : 'lists';
        const needsSync = await checkNeedsPriceSync(uid, selected, lastUpdated, listsField);
        if (cancelled) return; // Check if cancelled after async operation

        if (needsSync) {
          setPriceSyncing(true);
          console.log('[BuyingLists] Syncing prices for', items.length, 'items');

          const collectionName = active === 'boards' ? 'buyListsBoards' : 'buyLists';
          const result = await syncBuyListPrices(
            uid,
            selected,
            items,
            dp,
            charges,
            algoliaClientRef.current,
            collectionName
          );
          if (cancelled) {
            console.log('[BuyingLists] Sync cancelled after completion - list was switched');
            setPriceSyncing(false);
            return; // Don't update anything if cancelled
          }

          console.log('[BuyingLists] Price sync result:', result);

          const syncCompleted = result.failed === 0 && (result.synced > 0 || result.skipped > 0);

          // Reload items to reflect updated prices
          if (result.synced > 0) {
            const col = collection(db, collectionName, uid, selected);
            const q = query(col);
            const snap = await getDocs(q);
            if (cancelled) {
              console.log('[BuyingLists] Sync cancelled during item reload - list was switched');
              setPriceSyncing(false);
              return;
            }
            const updated = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setItems(updated);
            console.log('[BuyingLists] Reloaded items after syncing', result.synced, 'items');
          }

          if (syncCompleted) {
            await updateListLastUpdated(uid, selected, lastUpdated, listsField);
            console.log('[BuyingLists] Updated list lastUpdated timestamp after successful sync', {
              synced: result.synced,
              skipped: result.skipped,
            });
          } else {
            console.warn('[BuyingLists] Sync did not fully succeed; list lastUpdated was NOT updated.', result);
          }
        } else {
          console.log('[BuyingLists] No sync needed for list:', selected);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('[BuyingLists] Price sync error:', e);
        }
      } finally {
        if (!cancelled) {
          setPriceSyncing(false);
        }
      }
    })();

    // Cleanup function: cancel ongoing sync when list changes
    return () => {
      cancelled = true;
      console.log('[BuyingLists] Cancelling ongoing price sync due to list change');
    };
  }, [uid, selected, items, loading, active]);

  // Filtered items (search)
  const filtered = React.useMemo(() => {
    if (!search.trim()) return items;
    const s = search.trim().toLowerCase();
    return items.filter((it) =>
      String(it.brand || '').toLowerCase().includes(s) ||
      String(it.code || '').toLowerCase().includes(s)
    );
  }, [items, search]);

  // Reset to page 0 when search changes
  React.useEffect(() => {
    setCurrentPage(0);
  }, [search]);

  // Pagination calculations
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginatedItems = React.useMemo(() => {
    const start = currentPage * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, currentPage]);

  // Totals with discount applied
  const totalsMemo = React.useMemo(() => {
    let totalQty = 0;
    let totalValue = 0;
    let ceramicWeightGrams = 0;
    let ceramicListPrice = 0;

    const disc = Number(discountPct) || 0;
    const discMul = (100 - disc) / 100;

    for (const it of filtered) {
      const qty = getQty(it);
      const unitBase = computeItemPriceLocal(it, currency, pricing) || 0;
      const unit = unitBase * discMul;
      const material = String(it.material || '').toUpperCase();
      const weightCeramic = parseFloat(it.weightCeramic);
      const weightVal = Number.isFinite(weightCeramic) ? weightCeramic : 0;

      totalQty += qty;
      totalValue += unit * qty;

      if (material !== 'METAL') {
        ceramicWeightGrams += weightVal * qty;
        ceramicListPrice += unit * qty;
      }
    }

    const avgUnit = totalQty > 0 ? totalValue / totalQty : 0;

    let pricePerKg = 0;
    if (ceramicWeightGrams > 0) {
      const raw = ceramicListPrice / (ceramicWeightGrams / 1000);
      pricePerKg = Math.round(raw * 100) / 100;
    }

    return {
      totalQty,
      totalValue,
      avgUnit,
      ceramicWeightKg: ceramicWeightGrams / 1000,
      pricePerKg,
    };
  }, [filtered, pricing, currency, discountPct]);

  // Metals data fetch - handles both converters (PT/PD/RH) and boards (AU/AG/PD/CU)
  React.useEffect(() => {
    const roleKey = effectiveRole;
    const allowed = ['admin', 'managerplus'].includes(roleKey);
    if (!allowed) {
      setMetals({ pt: 0, pd: 0, rh: 0 });
      setBoardsMetals({ au: 0, ag: 0, pd: 0, cu: 0 });
      return;
    }
    if (!uid) return;

    const isBoardMode = active === 'boards';

    // For boards mode, use handleGetBoardsMetalsData2
    if (isBoardMode) {
      const baseObj = {};
      const entries = [];
      for (const it of items) {
        const key = it.id || it.objectID || it.objectId;
        if (!key) continue;
        baseObj[key] = it;
        entries.push([key, it]);
      }
      if (entries.length === 0) {
        setBoardsMetals({ au: 0, ag: 0, pd: 0, cu: 0 });
        return;
      }

      const arrayFromObject = entries.map(([key, value]) => ({ [key]: value }));
      const finalChunks = [];
      for (let i = 0; i < arrayFromObject.length; i += 200) {
        finalChunks.push(arrayFromObject.slice(i, i + 200));
      }

      const reqId = ++metalsFetchIdRef.current;
      setMetalsLoading(true);
      setBoardsMetals({ au: 0, ag: 0, pd: 0, cu: 0 });

      const functions = getFunctions(getApp(), 'us-central1');
      const call = httpsCallable(functions, 'handleGetBoardsMetalsData2');

      (async () => {
        try {
          let totalAU = 0, totalAG = 0, totalPD = 0, totalCU = 0, totalWeight = 0;

          const results = finalChunks.length > 0
            ? await Promise.all(
              finalChunks.map((chunkArr) => {
                const objectList = Object.assign({}, chunkArr);
                return call({ list: objectList, fullList: baseObj });
              })
            )
            : [];

          for (const res of results) {
            const d = res?.data || {};
            totalAU += Number(d.au) || 0;
            totalAG += Number(d.ag) || 0;
            totalPD += Number(d.pd) || 0;
            totalCU += Number(d.cu) || 0;
            totalWeight += Number(d.weight) || 0;
          }

          let next = { au: 0, ag: 0, pd: 0, cu: 0 };
          if (totalWeight > 0) {
            // AU/AG/PD: convert total grams back to average mg/kg
            const au = Math.round((totalAU * 1000) / totalWeight);
            const ag = Math.round((totalAG * 1000) / totalWeight);
            const pd = Math.round((totalPD * 1000) / totalWeight);
            // CU: average percentage per kg
            const cu = Math.round((totalCU / totalWeight) * 100) / 100;
            next = { au, ag, pd, cu };
          }

          if (reqId === metalsFetchIdRef.current) setBoardsMetals(next);
        } catch (e) {
          console.error('Boards metals fetch failed:', e);
          if (reqId === metalsFetchIdRef.current) setBoardsMetals({ au: 0, ag: 0, pd: 0, cu: 0 });
        } finally {
          if (reqId === metalsFetchIdRef.current) setMetalsLoading(false);
        }
      })();
      return;
    }

    // For converters mode, use handleGetMetalsData (original logic)
    const baseObj = {};
    const calcEntries = [];
    const otherEntries = [];
    for (const it of items) {
      const key = it.id || it.objectID || it.objectId;
      if (!key) continue;
      baseObj[key] = it;
      const mat = String(it.material || '').toUpperCase();
      if (mat === 'CALCULATOR') {
        calcEntries.push([key, it]);
      } else {
        otherEntries.push([key, it]);
      }
    }
    const hasAny = calcEntries.length + otherEntries.length > 0;
    if (!hasAny) { setMetals({ pt: 0, pd: 0, rh: 0 }); return; }

    let localPTg = 0, localPDg = 0, localRHg = 0, localWeightKg = 0;
    for (const [, it] of calcEntries) {
      const grams = Number(it?.weightCeramic) || 0;
      const ptPPM = Number(it?.pt) || 0;
      const pdPPM = Number(it?.pd) || 0;
      const rhPPM = Number(it?.rh) || 0;
      if (grams > 0) {
        localPTg += (ptPPM * grams) / 1_000_000;
        localPDg += (pdPPM * grams) / 1_000_000;
        localRHg += (rhPPM * grams) / 1_000_000;
        localWeightKg += grams / 1000;
      }
    }

    const arrayFromObject = otherEntries.map(([key, value]) => ({ [key]: value }));
    const finalChunks = [];
    for (let i = 0; i < arrayFromObject.length; i += 200) {
      finalChunks.push(arrayFromObject.slice(i, i + 200));
    }

    const reqId = ++metalsFetchIdRef.current;
    setMetalsLoading(true);
    setMetals({ pt: 0, pd: 0, rh: 0 });

    const functions = getFunctions(getApp(), 'us-central1');
    const call = httpsCallable(functions, 'handleGetMetalsData2');

    (async () => {
      try {
        let totalPT = localPTg, totalPD = localPDg, totalRH = localRHg, totalWeight = localWeightKg;

        const results = finalChunks.length > 0
          ? await Promise.all(
            finalChunks.map((chunkArr) => {
              const objectList = Object.assign({}, chunkArr);
              return call({ list: objectList, fullList: baseObj });
            })
          )
          : [];

        for (const res of results) {
          const d = res?.data || {};
          totalPT += Number(d.pt) || 0;
          totalPD += Number(d.pd) || 0;
          totalRH += Number(d.rh) || 0;
          totalWeight += Number(d.weight) || 0;
        }

        let next = { pt: 0, pd: 0, rh: 0 };
        if (totalWeight > 0) {
          const pt = Math.round((totalPT * 1000) / totalWeight);
          const pd = Math.round((totalPD * 1000) / totalWeight);
          const rh = Math.round((totalRH * 1000) / totalWeight);
          next = { pt, pd, rh };
        }

        if (reqId === metalsFetchIdRef.current) setMetals(next);
      } catch {
        if (reqId === metalsFetchIdRef.current) setMetals({ pt: 0, pd: 0, rh: 0 });
      } finally {
        if (reqId === metalsFetchIdRef.current) setMetalsLoading(false);
      }
    })();
  }, [items, uid, effectiveRole, active]);

  // Actions
  function handleSavePdf() {
    setPdfPickerOpen(true);
  }

  // Called when user selects language and options from the picker
  const onSelectPdfLanguage = async (languageCode, includePpm, includeMarketPrices) => {
    const userName = [
      (settings?.firstName || '').trim(),
      (settings?.lastName || '').trim(),
    ].filter(Boolean).join(' ') || '-';

    const isAdmin = ['admin', 'managerplus'].includes(effectiveRole);

    generateBuyingListPDF({
      items,
      listName: selected || t('buyingLists.title'),
      currency,
      discountPct,
      userName,
      totalValue: totalsMemo.totalValue,
      avgUnit: totalsMemo.avgUnit,
      totalQty: totalsMemo.totalQty,
      ceramicWeightKg: totalsMemo.ceramicWeightKg,
      pricePerKg: isAdmin ? totalsMemo.pricePerKg : undefined,
      metals: isAdmin && includePpm ? metals : undefined,
      dailyPrices: isAdmin && includeMarketPrices ? dailyPrices : undefined,
      computePrice: (item) => computeItemPriceLocal(item, currency, pricing),
      isAdmin,
      companyName: 'Gebruder Naim',
      language: languageCode,
    });
  };

  async function updateQty(itemId, nextTotal) {
    if (!uid || !selected) return;
    const normalized = String(nextTotal).replace(",", ".");
    const parsed = parseFloat(normalized);
    if (!Number.isFinite(parsed)) return;

    const v = parsed < 0 ? 0 : parsed;
    const collectionPath = active === 'boards' ? 'buyListsBoards' : 'buyLists';
    const ref = doc(db, collectionPath, uid, selected, itemId);

    if (v === 0) {
      await deleteDoc(ref);
      setItems((prev) => prev.filter((x) => x.id !== itemId));
    } else {
      await updateDoc(ref, { total: v });
      setItems((prev) =>
        prev.map((x) => (x.id === itemId ? { ...x, total: v } : x))
      );
    }
  }

  async function removeItem(itemId) {
    if (!uid || !selected) return;
    const collectionPath = active === 'boards' ? 'buyListsBoards' : 'buyLists';
    await deleteDoc(doc(db, collectionPath, uid, selected, itemId));
    setItems((prev) => prev.filter((x) => x.id !== itemId));
  }

  async function handleClearList() {
    if (!uid || !selected) return;
    setClearConfirmOpen(false);
    try {
      const collectionPath = active === 'boards' ? 'buyListsBoards' : 'buyLists';
      const col = collection(db, collectionPath, uid, selected);
      const snap = await getDocs(query(col));
      await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
      setItems([]);
    } catch (e) {
      console.error(e);
      alert('Failed to clear the list.');
    }
  }

  // Details drawer
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [detailsHit, setDetailsHit] = React.useState(null);

  const appId = process.env.REACT_APP_ALGOLIA_APP_ID;
  const apiKey = process.env.REACT_APP_ALGOLIA_SEARCH_KEY;
  const algoliaClientRef = React.useRef(null);
  React.useEffect(() => {
    try {
      if (appId && apiKey) {
        algoliaClientRef.current = algoliasearch(appId, apiKey);
      }
    } catch { }
  }, [appId, apiKey]);

  const defaultIndex = React.useMemo(() => IDX.converters, []);

  const fetchAlgoliaHit = React.useCallback(async (objectID, preferredIndex) => {
    const client = algoliaClientRef.current;
    if (!client || !objectID) return null;
    const candidates = [preferredIndex, defaultIndex, IDX.converters, IDX.metals, IDX.trucks, IDX.boards]
      .filter(Boolean)
      .filter((v, i, a) => a.indexOf(v) === i);
    for (const indexName of candidates) {
      try {
        // Use Algolia v5 API: client.getObject({ indexName, objectID })
        const obj = await client.getObject({ indexName, objectID: String(objectID) });
        if (obj) return obj;
      } catch { }
    }
    return null;
  }, [defaultIndex]);

  function normalizeItemForDetails(raw) {
    const out = { ...raw };
    try {
      const prices = raw?.prices || {};
      if (prices && typeof prices === 'object') {
        for (const k of Object.keys(prices)) {
          if (k && k.toLowerCase().startsWith('price')) {
            out[k] = prices[k];
          }
        }
      }
      out.dateUpdated = raw?.dateUpdated || raw?.dateModified || raw?.lastModifiedAt || null;
      out.objectID = raw?.objectID || raw?.objectId || raw?.id || null;
    } catch { }
    return out;
  }

  const openDetails = React.useCallback(async (rawItem) => {
    try {
      const objectID = rawItem?.objectID || rawItem?.objectId || rawItem?.id;
      const mat = String(rawItem?.material || '').toUpperCase();
      let preferredIndex = null;
      if (mat.includes('TRUCK')) preferredIndex = IDX.trucks;
      else if (mat.includes('METAL')) preferredIndex = IDX.metals;
      else if (mat.includes('BOARD') || mat.includes('ELECTRONIC')) preferredIndex = IDX.boards;
      else preferredIndex = IDX.converters;
      let hit = null;
      if (objectID) {
        hit = await fetchAlgoliaHit(String(objectID), preferredIndex);
      }
      setDetailsHit(normalizeItemForDetails(hit || rawItem));
      setDetailsOpen(true);
    } catch {
      setDetailsHit(normalizeItemForDetails(rawItem));
      setDetailsOpen(true);
    }
  }, [fetchAlgoliaHit]);

  const closeDetails = React.useCallback(() => {
    setDetailsOpen(false);
    setDetailsHit(null);
  }, []);

  // Item row component (app-style layout)
  function ItemRow({ item, onOpenDetails }) {
    const unitBase = computeItemPriceLocal(item, currency, pricing);
    const disc = Number(discountPct) || 0;
    const discMul = (100 - disc) / 100;
    const unit = typeof unitBase === 'number' ? unitBase * discMul : unitBase;
    const qtyNum = getQty(item);
    const lineTotal = typeof unit === 'number' ? unit * qtyNum : null;
    const [qtyText, setQtyText] = React.useState(String(qtyNum));

    React.useEffect(() => {
      const tv = Number(item?.total);
      const v = Number.isFinite(tv) && tv > 0 ? tv : 1;
      setQtyText(String(v));
    }, [item?.total]);

    const imgs = typeof item?.imgURL === 'string'
      ? item.imgURL.split(',').map((s) => s.trim()).filter(Boolean)
      : Array.isArray(item?.imgURL) ? item.imgURL : [];
    const img = imgs[0] || '';

    const isCalc = String(item.material).toUpperCase() === 'CALCULATOR';

    const commitQty = () => {
      const tv = (qtyText || '').trim();
      if (tv === '') { setQtyText(String(qtyNum)); return; }
      const parsed = parseFloat(tv.replace(',', '.'));
      if (!Number.isFinite(parsed) || parsed < 0) { setQtyText(String(qtyNum)); return; }
      updateQty(item.id, parsed);
      setQtyText(String(parsed));
    };

    // Get first code (app splits by comma)
    const displayCode = String(item.code || '').split(',')[0] || '-';
    // For boards mode, show category instead of brand (matching naim-app behavior)
    const isBoardMode = active === 'boards';
    const displayBrand = item.brand === 'CALCULATOR'
      ? `${((item.weightCeramic || 0) / 1000).toFixed(2)} KG`
      : isBoardMode
        ? (item.category || item.brand || '-')
        : (item.brand || '-');

    return (
      <div
        className="relative flex items-center gap-3 p-3 rounded-xl mb-2"
        style={{
          backgroundColor: isForest ? 'rgba(26,77,64,0.6)' : '#ffffff',
          border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : '#c8e6c9'}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}
      >
        {/* Left: Image */}
        <div
          className="w-[80px] h-[60px] flex-shrink-0 rounded-lg overflow-hidden cursor-pointer bg-white"
          onClick={() => {
            if (String(item.material || '').toUpperCase() === 'CALCULATOR') {
              navigate('/calculator', { state: { editItem: item } });
              return;
            }
            onOpenDetails?.(item);
          }}
        >
          {img ? (
            <img src={img} alt={displayCode} className="w-full h-full object-contain" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: th.text.tertiary }}>No img</div>
          )}
        </div>

        {/* Middle: Brand (bold), Code below */}
        <div className="flex-1 min-w-0">
          <div
            className="font-bold text-sm truncate cursor-pointer hover:underline"
            onClick={() => onOpenDetails?.(item)}
            style={{ color: isForest ? '#e8f5e9' : th.text.primary }}
          >
            {displayBrand.toUpperCase()}
          </div>
          <div className="text-xs truncate" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>
            {displayCode}
          </div>
        </div>

        {/* Right column: Price top, Qty + 3-dot bottom */}
        <div className="flex flex-col items-end justify-between h-full gap-2 flex-shrink-0">
          {/* Price - top right (shows total: price x qty) */}
          <div className="text-sm font-bold" style={{ color: '#4caf50' }}>
            {typeof lineTotal === 'number' ? formatPrice(lineTotal, currency) : '???'}
          </div>

          {/* Qty input + 3-dot menu */}
          <div className="flex items-center gap-1">
            {isCalc ? (
              <div
                className="flex items-center justify-center h-7 w-10 rounded text-sm"
                style={{
                  backgroundColor: isForest ? '#1a4d40' : '#ffffff',
                  border: `1px solid ${isForest ? 'rgba(255,255,255,0.2)' : '#c8e6c9'}`,
                  color: isForest ? '#e8f5e9' : th.text.primary
                }}
              >
                1
              </div>
            ) : (
              <input
                type="text"
                inputMode="decimal"
                className="h-7 w-10 text-center text-sm rounded outline-none"
                style={{
                  backgroundColor: isForest ? '#1a4d40' : '#ffffff',
                  border: `1px solid ${isForest ? 'rgba(255,255,255,0.2)' : '#c8e6c9'}`,
                  color: isForest ? '#e8f5e9' : th.text.primary
                }}
                value={qtyText}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^[0-9]*([.,][0-9]*)?$/.test(val) || val === '') setQtyText(val);
                }}
                onFocus={(e) => e.currentTarget.select()}
                onBlur={commitQty}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                  else if (e.key === 'Escape') { setQtyText(String(qtyNum)); e.currentTarget.blur(); }
                }}
              />
            )}
            {/* Delete button */}
            <button
              type="button"
              onClick={() => removeItem(item.id)}
              className="p-1.5 rounded hover:opacity-80"
              style={{ backgroundColor: '#ef4444' }}
              title={t('common.delete')}
            >
              <Trash2 size={16} color="#ffffff" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Discount input handler - only allow -100 to 100, no expressions
  const handleDiscountChange = (e) => {
    const val = e.target.value;
    if (val === '' || val === '-') {
      setDiscountPct(0);
      return;
    }
    if (/[+\-*/]/.test(val.slice(1))) return;
    const num = parseInt(val, 10);
    if (Number.isFinite(num)) {
      setDiscountPct(Math.max(-100, Math.min(100, num)));
    }
  };
  const deniedShadowColor = isForest ? 'rgba(0,0,0,0.55)' : (t?.shadow?.lg || 'rgba(76,175,80,0.2)');
  const deniedCardStyle = {
    backgroundColor: isForest ? 'rgba(15,58,48,0.9)' : th.surface.card,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.15)' : th.surface.cardBorder}`,
    color: (th.cardText?.primary) || th.text.primary,
    boxShadow: `0 20px 60px ${deniedShadowColor}`,
  };
  const deniedTitleStyle = { color: th.cardText?.primary || th.text.primary };
  const deniedBodyStyle = {
    color: isForest ? (th.cardText?.secondary || th.text.secondary) : (t.text?.secondary || th.cardText?.secondary || th.text.primary),
  };
  const deniedIconWrapperStyle = {
    backgroundColor: isForest ? 'rgba(239,68,68,0.18)' : 'rgba(239,68,68,0.12)',
    color: t.status?.error || '#ef4444',
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.2)' : 'rgba(239,68,68,0.2)'}`,
  };

  if (!ready) return <div className="p-6" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{t('common.loading')}</div>;
  if (!uid) return <div className="p-6" style={{ color: th.status?.error || '#ef4444' }}>{t('errors.signInRequired')}</div>;
  if ((settings?.discount || 'free').toLowerCase() === 'free') {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-6">
        <div className="max-w-lg w-full rounded-2xl p-6 text-center" style={deniedCardStyle}>
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full" style={deniedIconWrapperStyle}>
            <ShieldAlert size={28} aria-hidden="true" />
          </div>
          <h2 className="text-2xl font-semibold mb-3" style={deniedTitleStyle}>{t('buyingLists.accessDenied')}</h2>
          <p className="text-sm" style={deniedBodyStyle}>{t('buyingLists.accessDeniedMessage')}</p>
        </div>
      </div>
    );
  }

  const listEmpty = filtered.length === 0 && !loading;
  const pageBg = isForest ? '#0f3a30' : '#E8F5E9';

  return (
    <ScreenGuard>
      <div ref={listContainerRef} className="px-0 md:px-0 pt-[2px] overflow-x-hidden relative pb-4" style={{ color: isForest ? '#e8f5e9' : th.text.primary, backgroundColor: pageBg, minHeight: '100vh' }}>
        <div className="flex flex-col gap-3">
          {/* Row 1: Action buttons left, List dropdown + Settings right */}
          <div className="flex items-center justify-between" style={{ backgroundColor: pageBg }}>
            <div className="flex items-center gap-2">
              {/* Share - green circle */}
              <button
                onClick={() => setShareOpen(true)}
                className="w-10 h-10 rounded-full flex items-center justify-center hover:opacity-80"
                style={{ backgroundColor: '#4caf50' }}
                title={t('buyingLists.shareList')}
              >
                <Share2 size={20} color="#ffffff" />
              </button>
              {/* PDF - green circle */}
              <button
                onClick={handleSavePdf}
                className="w-10 h-10 rounded-full flex items-center justify-center hover:opacity-80"
                style={{ backgroundColor: '#4caf50' }}
                title={t('buyingLists.savePdf')}
              >
                <FileText size={20} color="#ffffff" />
              </button>
              {/* Clear - red circle */}
              <button
                onClick={() => setClearConfirmOpen(true)}
                className="w-10 h-10 rounded-full flex items-center justify-center hover:opacity-80"
                style={{ backgroundColor: '#ef4444' }}
                title={t('buyingLists.clearList')}
              >
                <Trash2 size={20} color="#ffffff" />
              </button>
              {/* Boards/Converters toggle - hidden on mobile, shown in header instead */}
              <button
                onClick={() => setShowBoards(!showBoards)}
                className="h-10 px-3 rounded-full hidden md:flex items-center justify-center hover:opacity-80 text-sm font-medium"
                style={{
                  backgroundColor: isForest ? 'rgba(26,77,64,0.7)' : '#ffffff',
                  border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : '#c8e6c9'}`,
                  color: isForest ? '#e8f5e9' : th.text.primary
                }}
              >
                {showBoards ? 'Converters' : 'Boards'}
              </button>
            </div>
            <div className="flex items-center gap-1">
              <ListSelector compact boardMode={active === 'boards'} />
              <button
                onClick={() => setManageOpen(true)}
                className="px-2 py-1 rounded-lg transition-colors"
                style={{
                  backgroundColor: isForest ? 'rgba(26,77,64,0.7)' : '#ffffff',
                  border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : '#c8e6c9'}`,
                  color: isForest ? '#e8f5e9' : th.text.primary,
                }}
                title={t('manageLists.title')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                  <path fillRule="evenodd" d="M7.84 1.804A1 1 0 018.82 1h2.36a1 1 0 01.98.804l.331 1.652a6.993 6.993 0 011.929 1.115l1.598-.54a1 1 0 011.186.447l1.18 2.044a1 1 0 01-.205 1.251l-1.267 1.113a7.047 7.047 0 010 2.228l1.267 1.113a1 1 0 01.206 1.25l-1.18 2.045a1 1 0 01-1.187.447l-1.598-.54a6.993 6.993 0 01-1.929 1.115l-.33 1.652a1 1 0 01-.98.804H8.82a1 1 0 01-.98-.804l-.331-1.652a6.993 6.993 0 01-1.929-1.115l-1.598.54a1 1 0 01-1.186-.447l-1.18-2.044a1 1 0 01.205-1.251l1.267-1.114a7.05 7.05 0 010-2.227L1.821 7.773a1 1 0 01-.206-1.25l1.18-2.045a1 1 0 011.187-.447l1.598.54A6.993 6.993 0 017.51 3.456l.33-1.652zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>

          {/* Row 2: Search left, Discount input right */}
          <div className="flex items-center gap-3" style={{ backgroundColor: pageBg }}>
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder={t('buyingLists.search')}
                className="w-full h-10 pl-4 pr-4 rounded-lg text-sm"
                style={{
                  backgroundColor: isForest ? '#1a4d40' : '#ffffff',
                  border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : '#c8e6c9'}`,
                  color: isForest ? '#e8f5e9' : '#000000',
                }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <input
              type="text"
              placeholder={t('buyingLists.discountPercent')}
              className="h-10 px-2 rounded-lg text-sm text-center"
              style={{
                width: '110px',
                backgroundColor: isForest ? '#1a4d40' : '#ffffff',
                border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : '#c8e6c9'}`,
                color: discountColor,
                fontWeight: discountPct !== 0 ? 'bold' : 'normal',
              }}
              value={discountPct === 0 ? '' : discountPct}
              onChange={handleDiscountChange}
            />
          </div>

          {/* Summary row: Value, Average/Unit, Weight + PT/PD/RH on desktop */}
          <div style={{ backgroundColor: pageBg }}>
            <div
              className="flex items-center justify-start gap-4 md:gap-6 rounded-xl px-4 py-3 flex-wrap"
              style={{
                backgroundColor: isForest ? 'rgba(26,77,64,0.6)' : '#ffffff',
                border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : '#c8e6c9'}`,
              }}
            >
              <div className="hidden md:flex flex-col">
                <span className="text-xs" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>{t('buyingLists.quantity')}:</span>
                <span className="text-sm font-bold" style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>{totalsMemo.totalQty % 1 === 0 ? totalsMemo.totalQty : totalsMemo.totalQty.toFixed(1)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>{t('buyingLists.totalValue')}:</span>
                <span className="text-sm font-bold" style={{ color: '#4caf50' }}>{formatPrice(totalsMemo.totalValue, currency)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>{t('buyingLists.avgUnit')}:</span>
                <span className="text-sm font-bold" style={{ color: '#4caf50' }}>{formatPrice(totalsMemo.avgUnit, currency)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>{t('buyingLists.weight')}:</span>
                <span className="text-sm font-bold" style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>{totalsMemo.ceramicWeightKg.toFixed(3)} kg</span>
              </div>
              {/* Metals - desktop only, for managerPlus/admin - shows AU/AG/PD/CU for boards, PT/PD/RH for converters */}
              {['admin', 'managerplus'].includes(effectiveRole) && (
                <>
                  <div className="hidden md:block w-px h-8 mx-1" style={{ backgroundColor: isForest ? 'rgba(255,255,255,0.1)' : '#c8e6c9' }} />
                  {active === 'boards' ? (
                    <>
                      <div className="hidden md:flex flex-col items-center">
                        <span className="text-xs" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>AU</span>
                        <span className="text-sm font-bold tabular-nums" style={{ color: '#EAB308' }}>
                          {metalsLoading ? '...' : boardsMetals.au}
                        </span>
                      </div>
                      <div className="hidden md:flex flex-col items-center">
                        <span className="text-xs" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>AG</span>
                        <span className="text-sm font-bold tabular-nums" style={{ color: '#a5d6a7' }}>
                          {metalsLoading ? '...' : boardsMetals.ag}
                        </span>
                      </div>
                      <div className="hidden md:flex flex-col items-center">
                        <span className="text-xs" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>PD</span>
                        <span className="text-sm font-bold tabular-nums" style={{ color: '#06B6D4' }}>
                          {metalsLoading ? '...' : boardsMetals.pd}
                        </span>
                      </div>
                      <div className="hidden md:flex flex-col items-center">
                        <span className="text-xs" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>CU</span>
                        <span className="text-sm font-bold tabular-nums" style={{ color: '#F97316' }}>
                          {metalsLoading ? '...' : `${boardsMetals.cu}%`}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="hidden md:flex flex-col items-center">
                        <span className="text-xs" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>PT</span>
                        <span className="text-sm font-bold tabular-nums" style={{ color: '#4F46E5' }}>
                          {metalsLoading ? '...' : metals.pt}
                        </span>
                      </div>
                      <div className="hidden md:flex flex-col items-center">
                        <span className="text-xs" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>PD</span>
                        <span className="text-sm font-bold tabular-nums" style={{ color: '#06B6D4' }}>
                          {metalsLoading ? '...' : metals.pd}
                        </span>
                      </div>
                      <div className="hidden md:flex flex-col items-center">
                        <span className="text-xs" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>RH</span>
                        <span className="text-sm font-bold tabular-nums" style={{ color: '#8B5CF6' }}>
                          {metalsLoading ? '...' : metals.rh}
                        </span>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* List content */}
          <div style={{ backgroundColor: pageBg }}>
            {priceSyncing && (
              <div className="flex items-center justify-center py-4 gap-2" style={{ color: '#22c55e' }}>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm font-medium">{t('buyingLists.syncingPrices') || 'Syncing prices...'}</span>
              </div>
            )}
            {loading ? (
              <div className="flex items-center justify-center py-8" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>
                {t('common.loading')}
              </div>
            ) : error ? (
              <div className="py-4" style={{ color: t.status?.error || '#ef4444' }}>{error}</div>
            ) : listEmpty ? (
              <div className="py-8 text-center" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>
                {t('buyingLists.noItemsInList')}
              </div>
            ) : (
              <>
                {paginatedItems.map((it) => (
                  <ItemRow key={it.id} item={it} onOpenDetails={openDetails} />
                ))}

                {/* Pagination - only show if more than 10 items */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-4 flex-wrap">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                      disabled={currentPage === 0}
                      className="px-3 py-2 rounded-lg text-sm font-medium transition-opacity disabled:opacity-40"
                      style={{ backgroundColor: '#4caf50', color: '#ffffff' }}
                    >
                      {t('buyingLists.prev')}
                    </button>

                    {/* Page numbers */}
                    {Array.from({ length: totalPages }, (_, i) => {
                      // Show first, last, current, and adjacent pages; ellipsis for gaps
                      const showPage = i === 0 || i === totalPages - 1 || Math.abs(i - currentPage) <= 1;
                      const showEllipsisBefore = i === currentPage - 2 && currentPage > 2;
                      const showEllipsisAfter = i === currentPage + 2 && currentPage < totalPages - 3;

                      if (showEllipsisBefore || showEllipsisAfter) {
                        return <span key={i} className="px-1" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>...</span>;
                      }
                      if (!showPage) return null;

                      return (
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className="w-8 h-8 rounded-lg text-sm font-medium transition-all"
                          style={{
                            backgroundColor: i === currentPage ? '#4caf50' : (isForest ? 'rgba(15,58,48,0.5)' : '#e8f5e9'),
                            color: i === currentPage ? '#ffffff' : (isForest ? '#e8f5e9' : th.text.primary),
                            border: i === currentPage ? 'none' : `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : '#c8e6c9'}`,
                          }}
                        >
                          {i + 1}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={currentPage >= totalPages - 1}
                      className="px-3 py-2 rounded-lg text-sm font-medium transition-opacity disabled:opacity-40"
                      style={{ backgroundColor: '#4caf50', color: '#ffffff' }}
                    >
                      {t('buyingLists.next')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Floating Quantity badge - bottom left (clickable for manager+ to show PT/PD/RH) */}
        {!listEmpty && !loading && (
          <button
            type="button"
            onClick={() => {
              if (['admin', 'managerplus'].includes(effectiveRole)) setQtyExpanded(!qtyExpanded);
            }}
            className="fixed bottom-4 left-4 flex flex-col items-center justify-center rounded-lg px-3 py-2 shadow-lg transition-all"
            style={{
              backgroundColor: isForest ? 'rgba(26,77,64,0.95)' : 'rgba(255,255,255,0.95)',
              border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : '#c8e6c9'}`,
              cursor: ['admin', 'managerplus'].includes(effectiveRole) ? 'pointer' : 'default',
            }}
          >
            <div className="flex items-center gap-1">
              <span className="text-xs" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>Quantity</span>
              {['admin', 'managerplus'].includes(effectiveRole) && (
                <svg
                  className={`w-3 h-3 transition-transform ${qtyExpanded ? 'rotate-180' : ''}`}
                  style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </div>
            <span className="text-lg font-bold" style={{ color: '#4caf50' }}>{Math.round(totalsMemo.totalQty)}</span>

            {/* Expanded metals section for manager+ - shows AU/AG/PD/CU for boards, PT/PD/RH for converters */}
            {qtyExpanded && ['admin', 'managerplus'].includes(effectiveRole) && (
              <div className="mt-2 pt-2 flex gap-3" style={{ borderTop: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : '#c8e6c9'}` }}>
                {active === 'boards' ? (
                  <>
                    <div className="flex flex-col items-center">
                      <span className="text-xs font-medium" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>AU</span>
                      <span className="text-sm font-medium tabular-nums" style={{ color: '#EAB308' }}>{boardsMetals.au}</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-xs font-medium" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>AG</span>
                      <span className="text-sm font-medium tabular-nums" style={{ color: '#a5d6a7' }}>{boardsMetals.ag}</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-xs font-medium" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>PD</span>
                      <span className="text-sm font-medium tabular-nums" style={{ color: '#06B6D4' }}>{boardsMetals.pd}</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-xs font-medium" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>CU</span>
                      <span className="text-sm font-medium tabular-nums" style={{ color: '#F97316' }}>{boardsMetals.cu}%</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex flex-col items-center">
                      <span className="text-xs font-medium" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>PT</span>
                      <span className="text-sm font-medium tabular-nums" style={{ color: '#4F46E5' }}>{metals.pt}</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-xs font-medium" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>PD</span>
                      <span className="text-sm font-medium tabular-nums" style={{ color: '#06B6D4' }}>{metals.pd}</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-xs font-medium" style={{ color: isForest ? '#a5d6a7' : '#6b7280' }}>RH</span>
                      <span className="text-sm font-medium tabular-nums" style={{ color: '#8B5CF6' }}>{metals.rh}</span>
                    </div>
                  </>
                )}
                {metalsLoading && <span className="text-xs" style={{ color: '#a5d6a7' }}>...</span>}
              </div>
            )}
          </button>
        )}

        {/* Modals */}
        <ManageListsModal open={manageOpen} onClose={() => setManageOpen(false)} boardMode={active === 'boards'} />
        <ShareListModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          listItems={items}
          listName={selected}
          totalValue={totalsMemo.totalValue}
          currency={currency}
          boardMode={active === 'boards'}
        />
        <DetailsSheetLite open={detailsOpen} onClose={closeDetails} hit={detailsHit} boardMode={active === 'boards'} />
        <LanguagePickerModal
          open={pdfPickerOpen}
          onClose={() => setPdfPickerOpen(false)}
          onSelect={onSelectPdfLanguage}
          title={t('pdf.selectLanguage')}
          showPpmOption={['admin', 'managerplus'].includes(effectiveRole)}
          showMarketPricesOption={['admin', 'managerplus'].includes(effectiveRole)}
          metalsLoading={metalsLoading}
        />
      </div>

      {/* Clear List Confirmation Modal */}
      <ConfirmModal
        open={clearConfirmOpen}
        onClose={() => setClearConfirmOpen(false)}
        onConfirm={handleClearList}
        title={t('buyingLists.clearList')}
        message={t('buyingLists.confirmClearList')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        destructive={true}
      />
    </ScreenGuard>
  );
}

